/**
  This Wizard is very inspired by the one in the formik examples
  https://github.com/formium/formik/blob/b9247e7b571cc2cd304d6eadb189b725c916b457/examples/MultistepWizard.js
*/
import React, { useState, ReactElement } from 'react'
import { Formik, Form } from 'formik'
import { Button } from 'components'
import { WizardStepProps } from './WizardStep'
import { WizardTabs } from './WizardTabs'
import classes from './Wizard.module.scss'
import { useTranslation } from 'react-i18next'

interface WizardProps {
  children: Array<ReactElement<WizardStepProps>>
  initialValues: any
  onSubmit: Function
}

export const Wizard: React.FunctionComponent<WizardProps> = ({
  children,
  initialValues,
  onSubmit,
}) => {
  const { t } = useTranslation()
  const [stepNumber, setStepNumber] = useState(0)
  const steps = React.Children.toArray(children) as Array<
    ReactElement<WizardStepProps>
  >
  const [snapshot, setSnapshot] = useState(initialValues)
  const [maxVisited, setMaxVisited] = useState(stepNumber)

  const step = steps[stepNumber]
  const totalSteps = steps.length
  const isLastStep = stepNumber === totalSteps - 1

  const setStep = (n: number) => {
    setMaxVisited(Math.max(n, maxVisited))
    setStepNumber(n)
  }

  const next = (values: any) => {
    setSnapshot(values)
    setStep(Math.min(stepNumber + 1, totalSteps - 1))
  }

  const previous = (values: any) => {
    setSnapshot(values)
    setStep(Math.max(stepNumber - 1, 0))
  }

  const handleSubmit = async (values: any, bag: any) => {
    if (step.props.onSubmit) {
      await step.props.onSubmit(values, bag)
    }
    if (isLastStep) {
      return onSubmit(values, bag)
    } else {
      bag.setTouched({})
      next(values)
    }
  }

  return (
    <Formik
      initialValues={snapshot}
      onSubmit={handleSubmit}
      validationSchema={step.props.validationSchema}
    >
      {(formik) => (
        <Form>
          <WizardTabs
            stepNames={steps.map((step) => t(step.props.name))}
            stepNumber={stepNumber}
            maxVisited={maxVisited}
            isValid={formik.isValid}
            setStepNumber={setStepNumber}
          />

          <div className={classes.step}>
            {steps.map((step, index) => (index === stepNumber ? step : null))}
          </div>

          <div className={classes.buttonContainer}>
            <Button
              variant="tertiary"
              size="medium"
              icon="chevron-left"
              disabled={stepNumber === 0}
              onClick={() => previous(formik.values)}
            >
              {t('global.back')}
            </Button>

            <Button
              id="button-workflow-e2e"
              size="medium"
              disabled={formik.isSubmitting}
              type="submit"
            >
              {isLastStep ? t('global.submit') : t('global.next')}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  )
}
